/**
 * AgentApiClient -- HTTP client for /api/v1/Agents
 * and direct n8n webhook integration.
 *
 * @module infrastructure/api/AgentApiClient
 */

import { ApiBase } from './ApiBase.js';
import { AppConfig } from '../../config.js';
import { getSelectedBusiness } from '../negocioElegido.js';

const BASE = '/api/v1/Agents';
const N8N_TIMEOUT = 15_000;
// La API corta el flujo a los 20 s (503 CONSULTA_NO_DISPONIBLE); el panel espera un poco mas para leer ese 503.
const CONSULTA_TIMEOUT = 25_000;

/**
 * Por que no salio la consulta, en frase de persona. La API manda su propio mensaje
 * cuando lo tiene (sabe mas del caso); si no, se dice por causa.
 */
function motivoDeConsulta(res) {
  const code = res?.code;
  const propio = typeof res?.error === 'string' ? res.error : '';
  const d = res?.details || {};
  const clave = String(d.code || d.error || d.codigo || '').toUpperCase();
  if (code === 'TIMEOUT') return 'Argemiro tardo demasiado en responder. Intentalo de nuevo en un momento.';
  if (code === 'NETWORK') {
    return AppConfig.env === 'dev'
      ? `No pudimos hablar con la API: estas en dev, apuntando a ${AppConfig.apiBase}.`
      : 'No pudimos hablar con la API ahora. Revisa la conexion e intentalo de nuevo.';
  }
  if (code === 503 || clave === 'CONSULTA_NO_DISPONIBLE') return 'Argemiro no esta disponible en este momento. Intentalo de nuevo en un momento.';
  if (code === 400 || clave === 'CONSULTA_INVALIDA') return 'No pude leer esa pregunta. Escribela de otra forma (hasta 2000 letras).';
  if (code === 403) return 'Tu usuario no tiene permiso para consultar los datos de este negocio.';
  return propio || 'No pude consultar ahora mismo.';
}

export const AgentApiClient = {
  async list(page = 1, pageSize = 20) {
    return ApiBase.get(`${BASE}?page=${page}&pageSize=${pageSize}`);
  },

  /**
   * Lo que PUEDE un agente: sus llaves activas (scopes, negocios y herramientas concedidas) y las instrucciones que le
   * aplican (nexo-api PR 631, FICHA-PERSONA-AGENTE-T1). Solo admin. Sin secretos. Un 404 = la ruta aun no esta publicada.
   */
  async puede(code) {
    return ApiBase.get(`/api/v1/admin/agents/${encodeURIComponent(code)}/puede`);
  },

  async getById(id) {
    return ApiBase.get(`${BASE}/${id}`);
  },

  async create(agent) {
    return ApiBase.post(BASE, agent);
  },

  async update(id, data) {
    return ApiBase.put(`${BASE}/${id}`, data);
  },

  async remove(id) {
    return ApiBase.del(`${BASE}/${id}`);
  },

  /**
   * PANEL-AUDIOS-T1 fase A (bit #8233): historial de audios que Argemiro envio
   * por WhatsApp (fuente nexo_private.argemiro_audio_log).
   *
   * CONTRATO AUN NO LOCKEADO: propuesto a Ares en bitacora #8234
   * (GET /api/v1/argemiro/audios?limit=&desde=&hasta=). Mientras no exista,
   * devuelve ok:false con code NOT_IMPLEMENTED y el consumidor degrada con
   * banner honesto -- se enciende SOLO cuando la API responda (patron /saldos).
   * Tolera items[] directo o envuelto en data / data.items.
   */
  async listAudios({ limit = 50, desde = null, hasta = null } = {}) {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (desde) qs.set('desde', desde);
    if (hasta) qs.set('hasta', hasta);
    const res = await ApiBase.get(`/api/v1/argemiro/audios?${qs}`);
    if (!res.ok) {
      const txt = JSON.stringify(res.error || '');
      const t = txt.toLowerCase();
      // Regex anterior quedo roto (nots*found: se perdio el escape) y solo
      // acertaba por '404'. Con includes no hay escapes que se pierdan.
      if (t.includes('404') || t.includes('not found') || t.includes('no existe')) {
        return { ok: false, error: { code: 'NOT_IMPLEMENTED' } };
      }
      // El endpoint EXISTE y esta gateado (rol + negocio del token, gate de
      // Argy-A #8269): sin permiso NO es "falta construirlo".
      if (t.includes('401') || t.includes('403') || t.includes('token') || t.includes('autentic') || t.includes('autoriz') || t.includes('permis')) {
        return { ok: false, error: { code: 'FORBIDDEN' } };
      }
      return res;
    }
    const payload = res.data?.data ?? res.data ?? {};
    const items = Array.isArray(payload.items) ? payload.items
                : Array.isArray(payload) ? payload
                : Array.isArray(payload.data) ? payload.data : [];
    return { ok: true, data: items };
  },

  async sendChat(message, conversationId, { projectKey = null, narrative = 'technical' } = {}) {
    const url = `${AppConfig.n8nBase}/webhook/router-v0-1`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), N8N_TIMEOUT);

    try {
      const res = await ApiBase.fetchQueCambia(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          action: 'chat',
          message,
          external_id: `panel:${AppConfig.token?.substring(0, 8) || 'anon'}`,
          conversation_id: conversationId,
          narrative,
          ...(projectKey ? { project_id: projectKey } : {}),
          requestedBy: `panel:${AppConfig.token?.substring(0, 8) || 'anon'}`,
          protocol_version: '0.1'
        })
      });
      clearTimeout(timer);

      if (!res.ok) throw new Error(`n8n ${res.status}`);
      const data = await res.json();
      return { ok: true, data, source: 'n8n' };
    } catch (err) {
      clearTimeout(timer);
      return { ok: false, error: err.message, source: 'local' };
    }
  },

  /**
   * Una consulta de datos a Argemiro (capacidades del catalogo, sin LLM que clasifique).
   *
   * CONSULTA-NEXO-CANDADO-T1 paso 2 (A #20393): ya NO va directo al webhook de n8n,
   * que no pedia credencial. Va a la API con el JWT del panel, como todo lo demas:
   *   POST /api/v1/businesses/{negocio}/consulta-nexo
   * La API pone el negocio, quien pregunta y el external_id (si llegan, los pisa), asi
   * que el cuerpo ya no los manda. El 200 trae el JSON del flujo TAL CUAL: los que
   * llaman leen result.data igual que antes.
   *
   * Sin negocio en el selector no hay ruta: se dice, no se manda null.
   * Los errores salen en frase de persona y por causa (el que llama los pinta tal cual).
   *
   * @param {string} message - la pregunta tal como la escribio la persona
   * @param {string} conversationId
   * @param {object} opts - { domain, intent, params }
   * @returns {{ok:true, data, source:'api'} | {ok:false, error:string, code, source:'local'}}
   */
  async sendConsulta(message, conversationId, { domain = null, intent = null, params: queryParams = {} } = {}) {
    const negocio = getSelectedBusiness() || null;
    if (!negocio) {
      return { ok: false, code: 'SIN_NEGOCIO', error: 'Elige un negocio en el selector de arriba para consultar sus datos.', source: 'local' };
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CONSULTA_TIMEOUT);
    const res = await ApiBase.post(`/api/v1/businesses/${encodeURIComponent(negocio)}/consulta-nexo`, {
      message,
      domain,
      intent,
      query_params: { ...queryParams },
      conversation_id: conversationId,
      protocol_version: '0.2'
    }, { signal: controller.signal });
    clearTimeout(timer);
    if (res.ok) return { ok: true, data: res.data ?? {}, source: 'api' };
    return { ok: false, code: res.code, error: motivoDeConsulta(res), source: 'local' };
  },

  async sendAction(action, params = {}) {
    const url = `${AppConfig.n8nBase}/webhook/router-v0-1`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), N8N_TIMEOUT);

    try {
      const res = await ApiBase.fetchQueCambia(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          action,
          requestedBy: `panel:${AppConfig.token?.substring(0, 8) || 'anon'}`,
          protocol_version: '0.1',
          ...params
        })
      });
      clearTimeout(timer);

      if (!res.ok) throw new Error(`router ${res.status}`);
      const data = await res.json();
      return { ok: true, data, source: 'n8n' };
    } catch (err) {
      clearTimeout(timer);
      return { ok: false, error: err.message, source: 'local' };
    }
  }
};
